import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ChatBubbleLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { Fragment, useState, useEffect, useRef } from 'react'
import { useAuth } from '../../../contexts/AuthContext'
import { messageAPI } from '../../../services/api'
import { ProjectMessageWithDetails } from '../../../types'
import { formatRelativeTime } from '../../../utils/timeFormat'

interface MessageModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  recipientId: string
  recipientName: string
  recipientEmail: string
}

export default function MessageModal({
  isOpen,
  onClose,
  projectId,
  recipientId,
  recipientName,
  recipientEmail,
}: MessageModalProps) {
  const [messages, setMessages] = useState<ProjectMessageWithDetails[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Load conversation messages
  useEffect(() => {
    if (isOpen && projectId && recipientId) {
      loadMessages()
      // Mark conversation as read when opening
      messageAPI.markConversationAsRead(projectId, recipientId)
    }
  }, [isOpen, projectId, recipientId])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadMessages = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await messageAPI.getConversationMessages(projectId, recipientId)
      setMessages(response.data)
    } catch (error) {
      console.error('Error loading messages:', error)
      setError('Failed to load messages. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!newMessage.trim() || sending) return

    try {
      setSending(true)
      setError(null)
      const response = await messageAPI.sendMessage(projectId, {
        recipient_id: recipientId,
        message: newMessage.trim(),
        message_type: 'general',
      })

      // Add new message to the list
      setMessages(prev => [...prev, response.data])
      setNewMessage('')
    } catch (error) {
      console.error('Error sending message:', error)
      setError('Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: send message
        e.preventDefault()
        handleSendMessage()
      }
      // Just Enter: allow new line (default behavior)
    }
  }

  const { user } = useAuth()
  const currentUserId = user?.id

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between bg-blue-50 px-6 py-4 border-b">
                  <div className="flex items-center">
                    <ChatBubbleLeftIcon className="w-6 h-6 text-blue-600 mr-3" />
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        Message {recipientName}
                      </Dialog.Title>
                      <p className="text-sm text-gray-600">{recipientEmail}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={onClose}
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Messages List */}
                <div className="flex flex-col h-[32rem] lg:h-[40rem] overflow-hidden">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-600 text-sm">
                        {error}
                        <button
                          onClick={loadMessages}
                          className="ml-2 underline hover:no-underline"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {loading ? (
                      <div className="text-center text-gray-500 py-8">Loading messages...</div>
                    ) : messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map(message => {
                        const isCurrentUser = message.sender_id === currentUserId
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-md lg:max-w-2xl px-4 py-2 rounded-lg ${
                                isCurrentUser
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-200 text-gray-900'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {message.message}
                              </p>
                              <p
                                className={`text-xs mt-1 ${
                                  isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                                }`}
                              >
                                {formatRelativeTime(message.created_at)}
                              </p>
                            </div>
                          </div>
                        )
                      })
                    )}
                    {/* Auto-scroll target */}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="border-t bg-gray-50 p-4">
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <div className="flex-1 relative">
                        <textarea
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Type your message... (Shift + Enter to send)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[40px] max-h-[120px]"
                          disabled={sending}
                          rows={1}
                          style={{
                            height: 'auto',
                            minHeight: '40px',
                            maxHeight: '120px',
                          }}
                          onInput={e => {
                            const target = e.target as HTMLTextAreaElement
                            target.style.height = 'auto'
                            target.style.height = Math.min(target.scrollHeight, 120) + 'px'
                          }}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center self-end"
                        title="Send message (Shift + Enter)"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                      </button>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
